/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../utils/language.utils';
import { applyThemeToDocument } from '../context/theme-context';
import { Loading } from './Loading';
import { GlobalTooltipHost } from './shared/GlobalTooltipHost';
import { normalizePageSize } from '../config/pagination.config';
import {
  applyModalControlPlacementPreference,
  readModalControlPlacementPreference,
} from '../utils/modal-control-placement.utils';

const LANG_LOCALE_MAP: Record<string, string> = {
  tr: 'tr-TR', en: 'en-US', ar: 'ar', de: 'de-DE', fr: 'fr-FR',
  es: 'es-ES', it: 'it-IT', ru: 'ru-RU', zh: 'zh-CN', ja: 'ja-JP',
};

const PREFS_CACHE_KEY = 'qubepro_ui_prefs_ts';
const PREFS_STALE_MS = 5 * 60 * 1000; // 5 dakika sonra API'den tekrar doğrula
const SETTINGS_FETCH_TIMEOUT_MS = 30000;
/** isAuthenticated anlık false olunca bootstrap sıfırlamayı geciktir (/settings/user fırtınası önlemi) */
const UI_PREFS_AUTH_OFF_MS = 420;

let settingsUserInflight: Promise<{ data?: { data?: unknown } }> | null = null;

function fetchSettingsUserOnce(): Promise<{ data?: { data?: unknown } }> {
  if (settingsUserInflight) {
    return settingsUserInflight;
  }
  settingsUserInflight = axios
    .get('/api/settings/user', { timeout: SETTINGS_FETCH_TIMEOUT_MS })
    .finally(() => {
      settingsUserInflight = null;
    });
  return settingsUserInflight;
}

function isCacheStale(): boolean {
  try {
    const ts = Number(localStorage.getItem(PREFS_CACHE_KEY) || '0');
    return Date.now() - ts > PREFS_STALE_MS;
  } catch {
    return true;
  }
}

function touchCache(): void {
  try {
    localStorage.setItem(PREFS_CACHE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

/**
 * Kullanıcı ayarlarını localStorage'a yazar ve ilgili context/event'leri günceller.
 * İlk mount'ta (blocking = true) ve arka planda (blocking = false) tekrar kullanılır.
 */
function applySettings(
  settings: Record<string, any>,
  ctx: {
    theme: string;
    setTheme: (t: 'light' | 'dark') => void;
    language: string;
    applyLanguageLocal: (l: SupportedLanguage) => void;
  }
): void {
  // Tema
  const dbTheme = settings.theme === 'dark' || settings.theme === 'light' ? settings.theme : null;
  if (dbTheme) {
    applyThemeToDocument(dbTheme);
    if (dbTheme !== ctx.theme) ctx.setTheme(dbTheme);
  }

  // Form pencere kontrolleri — sistem/macOS/Windows tercihi.
  applyModalControlPlacementPreference(settings.modalControlPlacement);

  // Dil (localStorage)
  const dbLang = typeof settings.language === 'string' && SUPPORTED_LANGUAGES.includes(settings.language as any) ? settings.language : null;
  if (dbLang) {
    try {
      localStorage.setItem('qubepro_language', dbLang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = LANG_LOCALE_MAP[dbLang] || 'en-US';
    if (dbLang !== ctx.language) {
      ctx.applyLanguageLocal(dbLang as SupportedLanguage);
    }
  }

  // Tarih formatı — eski anahtarı da temizle
  const df = typeof settings.dateFormat === 'string' ? settings.dateFormat.trim() : '';
  if (df) {
    try {
      localStorage.setItem('qubepro_userSetting_dateFormat', df);
      localStorage.removeItem('qubepro_userSetting_date_format');
    } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent('qubepro-user-setting-changed', { detail: { key: 'dateFormat', value: df } }));
    } catch { /* ignore */ }
  }

  // Saat formatı
  const tfRaw = typeof settings.timeFormat === 'string' ? settings.timeFormat.trim().toLowerCase() : '';
  const tf = tfRaw === '12h' || tfRaw === '24h' ? tfRaw : '24h';
  try {
    localStorage.setItem('qubepro_userSetting_timeFormat', tf);
    localStorage.removeItem('qubepro_userSetting_time_format');
  } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent('qubepro-user-setting-changed', { detail: { key: 'timeFormat', value: tf } }));
  } catch { /* ignore */ }

  // Zaman dilimi
  const tz = typeof settings.timezone === 'string' ? settings.timezone.trim() : '';
  if (tz) {
    try { localStorage.setItem('qubepro_userSetting_timezone', tz); } catch { /* ignore */ }
  }

  // Sayfa boyutu — eski anahtarı da güncelle
  const ps = normalizePageSize(settings.pageSize);
  try {
    localStorage.setItem('qubepro_userSetting_pageSize', String(ps));
  } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent('qubepro-user-setting-changed', { detail: { key: 'pageSize', value: ps } }));
  } catch { /* ignore */ }

  touchCache();
}

/**
 * Auth sonrası kullanıcı UI tercihlerini
 * (theme/language/dateFormat/timeFormat/timezone/pageSize/modalControlPlacement)
 * tek seferde bootstrap eder.
 *
 * Strateji:
 * 1. localStorage'da tercihler varsa UI'ı hemen göster (flicker yok).
 * 2. Cache PREFS_STALE_MS'den eskiyse veya boşsa, arka planda API'den çek ve güncelle.
 * 3. Ürün geçişlerinde (aynı origin) localStorage zaten paylaşımlı olduğundan tema/dil tutarlı kalır.
 * 4. Farklı origin'de (subdomain) ise localStorage boş olacağından ilk yüklemede API'den çekilir.
 */
export function UiPreferencesGate({ children }: { children: React.ReactNode }) {
  const { isInitialized, isAuthenticated, language, applyLanguageLocal } = useAuth();
  const { theme, setTheme } = useTheme();

  const [authStable, setAuthStable] = useState(isAuthenticated);
  useEffect(() => {
    if (isAuthenticated) {
      setAuthStable(true);
      return;
    }
    const t = window.setTimeout(() => setAuthStable(false), UI_PREFS_AUTH_OFF_MS);
    return () => window.clearTimeout(t);
  }, [isAuthenticated]);

  const [ready, setReady] = useState(true);
  const fetchingRef = useRef(false);
  const bootstrappedRef = useRef(false);
  const themeRef = useRef(theme);
  const languageRef = useRef(language);
  const setThemeRef = useRef(setTheme);
  const applyLanguageLocalRef = useRef(applyLanguageLocal);
  themeRef.current = theme;
  languageRef.current = language;
  setThemeRef.current = setTheme;
  applyLanguageLocalRef.current = applyLanguageLocal;

  useLayoutEffect(() => {
    applyModalControlPlacementPreference(
      readModalControlPlacementPreference(),
    );
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    if (!authStable) {
      setReady(true);
      fetchingRef.current = false;
      bootstrappedRef.current = false;
      return;
    }

    if (bootstrappedRef.current) {
      return;
    }

    // localStorage'da temel tercihler zaten varsa UI'ı hemen göster
    const hasTheme = theme === 'light' || theme === 'dark';
    const hasLang = SUPPORTED_LANGUAGES.includes(language);
    const cacheReady = hasTheme && hasLang;
    const stale = isCacheStale();

    if (cacheReady) {
      setReady(true);
      bootstrappedRef.current = true;

      // Cache güncel ise API çağrısı yapma
      if (!stale) return;

      // Cache eski ise arka planda güncelle (UI bloklanmaz)
      if (fetchingRef.current) return;
      fetchingRef.current = true;

      fetchSettingsUserOnce()
        .then(res => {
          const settings = res.data?.data ?? null;
          if (settings && typeof settings === 'object') {
            applySettings(settings, {
              theme: themeRef.current,
              setTheme: setThemeRef.current,
              language: languageRef.current,
              applyLanguageLocal: applyLanguageLocalRef.current,
            });
          }
        })
        .catch(() => { /* API'ye ulaşılamıyorsa eski cache ile devam */ })
        .finally(() => {
          fetchingRef.current = false;
          bootstrappedRef.current = true;
        });

      return;
    }

    // Cache boş: ilk bootstrap (blocking — sadece ilk sefer Loading göster)
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    let cancelled = false;
    setReady(false);

    (async () => {
      try {
        const res = await fetchSettingsUserOnce();
        const settings = res.data?.data ?? null;
        if (settings && typeof settings === 'object') {
          applySettings(settings, {
            theme: themeRef.current,
            setTheme: setThemeRef.current,
            language: languageRef.current,
            applyLanguageLocal: applyLanguageLocalRef.current,
          });
        }
      } catch {
        // API'ye ulaşılamıyorsa UI bloklanmamalı
      } finally {
        fetchingRef.current = false;
        if (!cancelled) {
          bootstrappedRef.current = true;
          setReady(true);
        }
      }
    })();

    return () => { cancelled = true; };
    // theme/language bağımlılıkta olmamalı: updateLanguage → dil değişimi → effect → /settings/user döngüsü
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, authStable]);

  if (!ready) {
    return <Loading fullScreen />;
  }
  return (
    <>
      <GlobalTooltipHost />
      {children}
    </>
  );
}
